import {
  buildCohortAssignmentReview,
  buildCohortMatch,
  collectConfirmedAssignments,
  sortCohortsForReview,
} from '../../domain/services/cohortAssignmentReviewService';

/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').CareModelCohortAssignmentReview
 * } CareModelCohortAssignmentReview */
/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').CareModelCohortClientAssignment
 * } CareModelCohortClientAssignment */
/** @typedef {
 *   import('./createCareModelCohortAssignmentReview.types').PrepareCareModelCohortAssignmentReviewParams
 * } PrepareCareModelCohortAssignmentReviewParams */
/** @typedef {
 *   import('./createCareModelCohortAssignmentReview.types').CollectCareModelCohortAssignmentsParams
 * } CollectCareModelCohortAssignmentsParams */
/** @typedef {
 *   import('./createCareModelCohortAssignmentReview.types').AssignCareModelCohortClientsParams
 * } AssignCareModelCohortClientsParams */
/** @typedef {
 *   import('./createCareModelCohortAssignmentReview.types').CareModelCohortAssignmentReviewUseCase
 * } CareModelCohortAssignmentReviewUseCase */

/**
 * @param {Function} assignClientsToCohortsApi
 * @param {Function} hydrateFilters
 * @param {Function} getFilteredElements
 * @returns {CareModelCohortAssignmentReviewUseCase}
 */
// eslint-disable-next-line import/prefer-default-export
export function createCareModelCohortAssignmentReview(
  assignClientsToCohortsApi,
  hydrateFilters,
  getFilteredElements,
) {
  /**
   * @param {PrepareCareModelCohortAssignmentReviewParams} params
   * @returns {CareModelCohortAssignmentReview}
   */
  function prepareReview({ clients = [], cohorts = [], columns = [] }) {
    const sortedCohorts = sortCohortsForReview(cohorts);

    return buildCohortAssignmentReview({
      clients,
      getMatchesForClient(client) {
        return sortedCohorts.reduce((acc, cohort) => {
          const hydratedFilters = hydrateFilters(cohort.filters || {}, columns);
          const matchedClients = getFilteredElements([client], hydratedFilters);

          if (matchedClients.length === 0) {
            return acc;
          }

          acc.push(buildCohortMatch(cohort, hydratedFilters));
          return acc;
        }, []);
      },
    });
  }

  /**
   * @param {CollectCareModelCohortAssignmentsParams} params
   * @returns {CareModelCohortClientAssignment[]}
   */
  function collectAssignments({ suggestedAssignments = [], manualAssignments = [] }) {
    return collectConfirmedAssignments({
      suggestedAssignments,
      manualAssignments,
    });
  }

  /**
   * @param {AssignCareModelCohortClientsParams} params
   * @returns {Promise<void>}
   */
  async function assignClients({ assignments = [] }) {
    if (!assignments.length) {
      return;
    }

    await assignClientsToCohortsApi({
      assignments,
    });
  }

  return {
    prepareReview,
    collectAssignments,
    assignClients,
  };
}
